import * as moment from "moment";
import { Moment } from "moment";
import { TrademarkFamily } from "./trademarkFamily";

export enum TrademarkStatus {
  UNFILED = 'UNFILED',
  OPPOSED = 'OPPOSED',
  PENDING = 'PENDING',
  FILED = 'FILED',
  REGISTERED = 'REGISTERED',
  ENDED = 'ENDED',
  EXPIRED = 'EXPIRED',
  UNKNOWN = 'UNKNOWN',
  PUBLISHED = 'PUBLISHED',
  COMMON_LAW = 'COMMONLAW'
}

export enum TrademarkType {
  SHAPE_3D = 'SHAPE_3D',
  COLOUR = 'COLOUR',
  COLOURS_COMBINATION = 'COLOURS_COMBINATION',
  COMBINED = 'COMBINED',
  FIGURATIVE = 'FIGURATIVE',
  GENERAL = 'GENERAL',
  HOLOGRAM = 'HOLOGRAM',
  KENNFADEN = 'KENNFADEN',
  MOTION = 'MOTION',
  MULTIMEDIA = 'MULTIMEDIA',
  NUMBER = 'NUMBER',
  OLFACTORY = 'OLFACTORY',
  OTHER = 'OTHER',
  PACKING = 'PACKING',
  PATTERN = 'PATTERN',
  POSITION = 'POSITION',
  SOUND = 'SOUND',
  STYLIZED_CHARACTERS = 'STYLIZED_CHARACTERS',
  UNDEFINED = 'UNDEFINED',
  WORD = 'WORD'
}

export enum TrademarkSynchronizationStatus {
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED'
}

// The last four: a mark was found but disagrees on this point, a person has to look from the trademark page
export enum TrademarkSynchronizationFailure {
  NOT_FOUND = 'NOT_FOUND',
  AMBIGUOUS = 'AMBIGUOUS',
  UNAVAILABLE = 'UNAVAILABLE',
  INCOMPLETE = 'INCOMPLETE',
  APPLICATION_DATE_DIFFERS = 'APPLICATION_DATE_DIFFERS',
  CLASSES_DIFFER = 'CLASSES_DIFFER',
  NAME_DIFFERS = 'NAME_DIFFERS',
  NUMBER_IN_OTHER_FIELD = 'NUMBER_IN_OTHER_FIELD',
  NOT_CONFIRMED = 'NOT_CONFIRMED'
}

// Values of the synchronization filter: a status, or NEVER for trademarks without any attempt
export enum TrademarkSynchronizationFilter {
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  NEVER = 'NEVER'
}

export interface TrademarkSynchronization {
  date: string;
  status: TrademarkSynchronizationStatus;
  failure?: TrademarkSynchronizationFailure;
  lastSuccessDate?: string;
  // True when the last synchronisation replaced the application date
  applicationDateChanged?: boolean;
}

export interface Trademark {
  _id?: string;
  identifierNumber: string;
  registrationNumber?: string;
  applicationNumber?: string;
  countryOfDesignation: string;
  applicationLanguageCode?: string;
  kind?: string;
  type?: string;
  ST13?: string;
  niceClassification?: {
    classNumber: number,
    details?: string
  }[];
  applicationDate?: Moment;
  publicationDate?: Moment;
  registrationDate?: Moment;
  oppositionEndDate?: Moment;
  designation?: boolean;
  designations?: TrademarkDTO[];
  expirationDate?: Moment;
  reproductionURI?: string;
  name?: string;
  description?:string;
  status?: TrademarkStatus;
  organizationId?: string;
  propertyOffice?: {
    ref: string;
    name: string;
    code: string;
    logo?: string;
  };
  family?: {
    manual?: boolean,
    ref?: string | TrademarkFamily,
    mode?: 'auto' | 'create' | 'select' | 'column'
  },
  embeddings?: number[]
  clientReference?: string
  // Absent when the trademark has never been synchronised with the office registers
  synchronization?: TrademarkSynchronization
}

export type TrademarkDTO = Omit<Trademark, '_id' | 'organizationId'>;
export type TrademarkDTOSearch = TrademarkDTO & { error: boolean };

// What an office answer has in common with the trademark; a missing key cannot be compared
export interface CandidateChecks {
  sameTerritory: boolean;
  sameNumber: boolean;
  sameApplicationDate?: boolean;
  sameName?: boolean;
  sharedClass?: boolean;
}

// An office answer for a synchronisation from the trademark page
export type SynchronizationCandidate = TrademarkDTO & {
  // The answer the bulk synchronisation would take, at most one per search
  match: boolean;
  checks: CandidateChecks;
  // No class, a placeholder name, or classes without the wording the trademark has
  incomplete: boolean;
  // The office image shows nothing: the trademark keeps its own
  blankImage: boolean;
  // The office name is a placeholder or a describing word ("Device", "FIG."): the trademark keeps its own
  nameKept?: boolean;
};

export interface SynchronizationCandidates {
  failure?: TrademarkSynchronizationFailure;
  candidates: SynchronizationCandidate[];
}
